import React, { useState } from 'react';
import { X, Eye, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PlacementPhoto } from '@libs/models';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface PlacementPhotoPreviewProps {
  photo: PlacementPhoto | File;
  isUploading?: boolean;
  uploadError?: boolean;
  onRemove?: () => void;
  className?: string;
}

export const PlacementPhotoPreview: React.FC<PlacementPhotoPreviewProps> = ({
  photo,
  isUploading = false,
  uploadError = false,
  onRemove,
  className,
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [imageError, setImageError] = useState(false);

  const getImageUrl = () => {
    if ('url' in photo) {
      return photo.url;
    }
    return URL.createObjectURL(photo);
  };

  const getFileName = () => {
    if ('fileName' in photo) {
      return photo.fileName.split('/').pop() || 'Unknown';
    }
    return photo.name;
  };

  return (
    <>
      <div
        className={cn(
          'relative group border rounded-lg overflow-hidden bg-white shadow-sm',
          className,
        )}
      >
        {/* Image */}
        <div className="aspect-square relative">
          {!imageError ? (
            <img
              src={getImageUrl()}
              alt={getFileName()}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400">
              <X className="h-8 w-8" />
            </div>
          )}

          {/* Uploading Overlay */}
          {isUploading && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <div className="flex items-center space-x-2 text-white text-sm">
                <RotateCcw className="h-4 w-4 animate-spin" />
                <span>Uploading...</span>
              </div>
            </div>
          )}

          {/* Upload Error Overlay */}
          {uploadError && (
            <div className="absolute inset-0 bg-red-500 bg-opacity-50 flex items-center justify-center">
              <div className="flex flex-col items-center space-y-2 text-white text-sm">
                <X className="h-4 w-4" />
                <span>Upload failed</span>
              </div>
            </div>
          )}

          {/* Action Buttons Overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
            <div className="flex space-x-2">
              {!isUploading && !uploadError && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPreview(true)}
                  className="bg-white bg-opacity-90 hover:bg-opacity-100"
                >
                  <Eye className="h-4 w-4" />
                </Button>
              )}
              {onRemove && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onRemove}
                  className="bg-white bg-opacity-90 hover:bg-opacity-100 hover:bg-red-50 hover:text-red-600"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* File Info */}
        <div className="p-2 border-t">
          <p className="text-xs text-gray-600 truncate" title={getFileName()}>
            {getFileName()}
          </p>
          {'size' in photo && (
            <p className="text-xs text-gray-400">{(photo.size / 1024 / 1024).toFixed(1)} MB</p>
          )}
        </div>
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{getFileName()}</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center">
            <img
              src={getImageUrl()}
              alt={getFileName()}
              className="max-h-[70vh] max-w-full object-contain rounded-lg"
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
